import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Button,
} from "@mui/material";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MapProtectedRainforestArea, MapRainforestMarker } from "types/map";
import { formatSqFtToKm2 } from "libraries/map";
import MapReferenceCompareTooltip from "./MapReferenceCompareTooltip";
import MapMarkerRainforestInfoDetails from "./MapMarkerRainforestInfoDetails";

type Props = {
  protectedHumans: MapProtectedRainforestArea[];
  highlightHumans: boolean;
  onToggleHumans: () => void;
  isMobile: boolean;
};

const MapLegendRainforestPanelHumans = ({
  protectedHumans,
  highlightHumans,
  onToggleHumans,
  isMobile,
}: Props) => {
  const [selectedArea, setSelectedArea] =
    useState<MapProtectedRainforestArea | null>(null);

  const totalHumanSqFt = useMemo(
    () =>
      protectedHumans.reduce((sum, area) => sum + (area.squareFootage || 0), 0),
    [protectedHumans]
  );

  const handleOpenDetails = useCallback((area: MapProtectedRainforestArea) => {
    setSelectedArea(area);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedArea(null);
  }, []);

  if (protectedHumans.length === 0) return null;

  // Convert MapProtectedRainforestArea to MapRainforestMarker for the Dialog
  const markerForDetails: MapRainforestMarker | null = selectedArea
    ? {
        name: selectedArea.name,
        lat: 0,
        lng: 0,
        partners: [],
        details: selectedArea.details,
        picture: selectedArea.picture,
      }
    : null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: isMobile ? 16 : 18,
            height: isMobile ? 16 : 18,
            backgroundColor: "#e0e0e0",
            border: "2px solid grey",
            borderRadius: 0.5,
            flexShrink: 0,
          }}
        />
        <Typography
          variant={isMobile ? "subtitle2" : "subtitle1"}
          fontWeight="bold"
        >
          Territorial and rights protection
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          ml: isMobile ? 3.5 : 4,
          fontSize: isMobile ? "0.7rem" : undefined,
          lineHeight: isMobile ? 1.2 : undefined,
        }}
      >
        Protecting Indigenous Communities
      </Typography>

      <Box
        sx={{
          ml: isMobile ? 3.5 : 4,
          mt: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <SquareFootIcon
            sx={{ fontSize: "0.9rem", color: "text.secondary" }}
          />
          <MapReferenceCompareTooltip squareFootage={totalHumanSqFt}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
            >
              {formatSqFtToKm2(totalHumanSqFt)} total
            </Typography>
          </MapReferenceCompareTooltip>
        </Stack>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={highlightHumans}
              color="primary"
              onChange={onToggleHumans}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              View
            </Typography>
          }
          labelPlacement="start"
          sx={{ margin: 0 }}
        />
      </Box>

      {protectedHumans.map(
        (area, index) =>
          area.details && (
            <Box key={index} sx={{ mt: 1, ml: isMobile ? 3.5 : 4 }}>
              <Button
                color="success"
                size="small"
                startIcon={<InfoOutlinedIcon />}
                onClick={() => handleOpenDetails(area)}
                sx={{ p: 0 }}
              >
                {area.name} info
              </Button>
            </Box>
          )
      )}

      {markerForDetails && (
        <MapMarkerRainforestInfoDetails
          open={!!selectedArea}
          onClose={handleCloseDetails}
          marker={markerForDetails}
        />
      )}
    </Box>
  );
};

export default MapLegendRainforestPanelHumans;
